import type { FieldOption } from "./field-util";
import type { Locale } from "@/stores/ui-store";

// ISO 639-1 codes; display names + sort resolved in the app locale via
// Intl.DisplayNames. Ported from workshop/api/queries/options.js.

const ISO_639_1 =
  "aa ab ae af ak am an ar as av ay az ba be bg bh bi bm bn bo br bs ca ce ch co cr cs cu cv cy da de dv dz ee el en eo es et eu fa ff fi fj fo fr fy ga gd gl gn gu gv ha he hi ho hr ht hu hy hz ia id ie ig ii ik io is it iu ja jv ka kg ki kj kk kl km kn ko kr ks ku kv kw ky la lb lg li ln lo lt lu lv mg mh mi mk ml mn mr ms mt my na nb nd ne ng nl nn no nr nv ny oc oj om or os pa pi pl ps pt qu rm rn ro ru rw sa sc sd se sg si sk sl sm sn so sq sr ss st su sv sw ta te tg th ti tk tl tn to tr ts tt tw ty ug uk ur uz ve vi vo wa wo xh yi yo za zh zu".split(
    " ",
  );

const ISO_639_1_SET = new Set(ISO_639_1);

// Whether `tag` is a language the app can actually represent — the same
// vocabulary the profile's language select offers. genesis stores languageTag as
// a free string, so an import can propose anything: a CV extraction in
// particular hands back whatever the document appeared to say ("mylang
// kuruhasan"). Such a value is not a language, cannot be shown as one, and would
// persist as permanent junk. A regional tag is judged by its primary subtag, so
// "en-GB" still counts as English.
export function isKnownLanguageTag(tag: string | null | undefined): boolean {
  const primary = (tag ?? "").trim().toLowerCase().split(/[-_]/)[0] ?? "";
  return ISO_639_1_SET.has(primary);
}

function buildLanguageOptions(locale: Locale): readonly FieldOption[] {
  let dn: Intl.DisplayNames | null = null;
  try {
    dn = new Intl.DisplayNames([locale], { type: "language" });
  } catch {
    // Intl.DisplayNames not available - fall back to bare codes.
  }
  return ISO_639_1
    .map((code): FieldOption | null => {
      const name = dn ? dn.of(code) : null;
      if (!name || name === code) return null;
      return { value: code, label: `${name} (${code.toUpperCase()})` };
    })
    .filter((o): o is FieldOption => o !== null)
    .sort((a, b) => a.label.localeCompare(b.label, locale));
}

// Cached per locale - each list is ~180 entries and locale rarely changes.
const OPTIONS_CACHE = new Map<Locale, readonly FieldOption[]>();

export function getLanguageOptions(locale: Locale): readonly FieldOption[] {
  const cached = OPTIONS_CACHE.get(locale);
  if (cached) return cached;
  const built = buildLanguageOptions(locale);
  OPTIONS_CACHE.set(locale, built);
  return built;
}

// Label for a stored ISO 639-1 tag in the app locale; raw tag if unknown.
export function languageLabel(tag: string | null | undefined, locale: Locale): string {
  if (!tag) return "";
  return getLanguageOptions(locale).find((o) => o.value === tag)?.label ?? tag;
}

export const CEFR_OPTIONS: readonly FieldOption[] = (["A1", "A2", "B1", "B2", "C1", "C2"] as const).map((v) => ({
  value: v,
  label: v,
}));

// Native speaker and a CEFR level are mutually exclusive - a native speaker has no
// foreign-language proficiency rung. Ticking one clears the other, live, so a form can
// never hold (and a row can never display) both at once. Shared between the add form
// (AddForm.tsx, live while the dialog is still open) and the entries-state layer
// (use-profile-entries-state.ts, edits to already-saved rows) - it's a no-op for any
// other entry kind since only language fields carry these two keys.
export function coupleLanguagePatch<T extends Record<string, unknown>>(patch: T): T {
  if (patch.nativeSpeaker === true) return { ...patch, cefr: "" };
  if (typeof patch.cefr === "string" && patch.cefr !== "") {
    return { ...patch, nativeSpeaker: false };
  }
  return patch;
}
